import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { updateStrutturaConfig } from "../config/strutturaConfig";
import {
  getLocalActiveStrutturaId,
  resolveLocalActiveStrutturaId,
  setLocalActiveStrutturaId,
} from "../utils/activeStrutturaLocal";
import { installMaintenanceInterceptor } from "../utils/maintenanceAxios";

installMaintenanceInterceptor();

export const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_PERMISSIONS = {
  super_admin: [
    "fascicolo:read", "fascicolo:create", "fascicolo:update", "fascicolo:delete", "fascicolo:export",
    "pai:read", "pai:create", "pai:update", "pai:delete",
    "valutazione:read", "valutazione:create", "valutazione:update", "valutazione:delete",
    "documento:read", "documento:upload", "documento:delete",
    "diario:read", "diario:create", "diario:delete",
    "diario_personale:read", "diario_personale:create",
    "admin:read", "admin:update",
    "users:read", "users:create", "users:update", "users:delete",
    "audit:read", "export:all",
    "struttura:read", "struttura:update",
    "billing:read", "billing:update",
    "contabile:read",
    "comunicazione:read", "comunicazione:send", "comunicazione:manage_templates",
    "centro_diurno:read", "centro_diurno:manage",
  ],
  amministrativo: [
    "fascicolo:read", "fascicolo:create", "fascicolo:update",
    "pai:read",
    "valutazione:read",
    "documento:read", "documento:upload",
    "diario:read",
    "diario_personale:read", "diario_personale:create",
    "admin:read", "admin:update",
    "users:read", "users:create", "users:update",
    "audit:read",
    "struttura:read", "struttura:update",
    "billing:read", "billing:update",
    "contabile:read",
    "comunicazione:read", "comunicazione:send", "comunicazione:manage_templates",
    "centro_diurno:read", "centro_diurno:manage",
  ],
  coordinatore: [
    "fascicolo:read", "fascicolo:create", "fascicolo:update",
    "pai:read", "pai:create", "pai:update",
    "valutazione:read", "valutazione:create", "valutazione:update",
    "documento:read", "documento:upload",
    "diario:read", "diario:create",
    "diario_personale:read", "diario_personale:create",
    "admin:read", "struttura:read",
    "audit:read",
    "comunicazione:read", "comunicazione:send",
    "centro_diurno:read", "centro_diurno:manage",
  ],
  operatore: [
    "fascicolo:read",
    "pai:read",
    "valutazione:read",
    "documento:read",
    "diario:read", "diario:create",
    "struttura:read",
  ],
  // Legacy roles mapping
  Responsabile: "super_admin",
  admin: "super_admin",
  user: "operatore",
};

const normalizeRole = (role) => {
  if (typeof ROLE_PERMISSIONS[role] === "string") {
    return ROLE_PERMISSIONS[role];
  }
  return role || "operatore";
};

const getPermissionsForRole = (role) => {
  const normalizedRole = normalizeRole(role);
  return ROLE_PERMISSIONS[normalizedRole] || ROLE_PERMISSIONS.operatore;
};

const enrichUserWithLocalStruttura = (userData) => {
  const activeStrutturaId = resolveLocalActiveStrutturaId(userData);
  const activeStruttura =
    userData.strutture?.find((s) => s.id === activeStrutturaId) ||
    userData.strutture?.[0] ||
    null;
  // Preferire id Mongo / struttura_id nativo rispetto a zohoRecordId legacy
  const effectiveId =
    activeStruttura?.id ||
    userData.struttura_id ||
    userData.activeStrutturaId ||
    activeStruttura?.zohoRecordId ||
    userData.zohoRecordId;
  return {
    ...userData,
    activeStrutturaId,
    facility_name: activeStruttura?.name || userData.facility_name,
    struttura_id: effectiveId,
    zohoRecordId: activeStruttura?.zohoRecordId || userData.zohoRecordId || effectiveId,
  };
};

const applyUserData = (userData, setUser, setPermissions) => {
  const enriched = enrichUserWithLocalStruttura(userData);
  setUser(enriched);
  // Usa i permessi effettivi forniti dal server (include override per-utente).
  // Fallback al calcolo locale dal ruolo per compatibilità durante il deploy.
  const effectivePermissions =
    Array.isArray(enriched.permissions) && enriched.permissions.length > 0
      ? enriched.permissions
      : getPermissionsForRole(enriched.role);
  setPermissions(effectivePermissions);

  const activeStruttura =
    enriched.strutture?.find((s) => s.id === enriched.activeStrutturaId) ||
    enriched.strutture?.[0] ||
    null;

  updateStrutturaConfig(activeStruttura || enriched);
};

export const AuthProvider = ({ children }) => {
  const [impersonating, setImpersonating] = useState(false);

  const getInitialTokens = () => {
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get("impersonate_token");
    const impRefresh = params.get("impersonate_refresh");
    if (impToken && impRefresh) {
      localStorage.setItem("affidya_token", impToken);
      localStorage.setItem("affidya_refresh_token", impRefresh);
      window.history.replaceState({}, "", window.location.pathname);
      return { token: impToken, refresh: impRefresh, isImpersonation: true };
    }
    return {
      token: localStorage.getItem("affidya_token"),
      refresh: localStorage.getItem("affidya_refresh_token"),
      isImpersonation: false,
    };
  };

  const initial = getInitialTokens();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(initial.token);
  const [refreshToken, setRefreshToken] = useState(initial.refresh);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (initial.isImpersonation) setImpersonating(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use((config) => {
      const activeId = getLocalActiveStrutturaId();
      if (activeId) {
        config.headers = config.headers || {};
        config.headers["X-Active-Struttura-Id"] = activeId;
      }
      return config;
    });
    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.data?.detail === "DEMO_EXTERNAL_DISABLED") {
          toast.info("Funzione disattivata nell'account Demo: nessun invio esterno è stato effettuato.");
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("affidya_token");
    localStorage.removeItem("affidya_refresh_token");
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setPermissions([]);
    setImpersonating(false);
    updateStrutturaConfig(null);
  }, []);

  /** Allinea token nello stato React con localStorage (es. dopo login 2FA che aggiorna solo lo storage). */
  const syncSessionFromStorage = useCallback(() => {
    const t = localStorage.getItem("affidya_token");
    const r = localStorage.getItem("affidya_refresh_token");
    setToken(t || null);
    setRefreshToken(r || null);
  }, []);

  const refreshAccessToken = useCallback(async () => {
    const storedRefresh = localStorage.getItem("affidya_refresh_token");
    if (!storedRefresh) return null;
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refresh_token: storedRefresh
      });
      const { token: newToken } = response.data;
      localStorage.setItem("affidya_token", newToken);
      setToken(newToken);
      return newToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
      return null;
    }
  }, [logout]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          applyUserData(response.data, setUser, setPermissions);
          if (
            response.data.account_status === "trial_expired" &&
            !window.location.pathname.startsWith("/trial-expired") &&
            !window.location.pathname.startsWith("/billing/")
          ) {
            window.location.replace("/trial-expired");
          }
        } catch (error) {
          console.error("Auth init error:", error);
          const status = error.response?.status;
          // Dopo ore/giorni l'access token è scaduto: il refresh resta in localStorage anche a browser chiuso
          const storedRefresh = localStorage.getItem("affidya_refresh_token");
          if (status === 401 && storedRefresh) {
            if (!refreshToken) {
              setRefreshToken(storedRefresh);
            }
            const newToken = await refreshAccessToken();
            if (newToken) {
              try {
                const retryResponse = await axios.get(`${API_URL}/api/auth/me`, {
                  headers: { Authorization: `Bearer ${newToken}` }
                });
                applyUserData(retryResponse.data, setUser, setPermissions);
              } catch (retryError) {
                logout();
              }
            } else {
              logout();
            }
          } else if (status === 401 || status === 403) {
            logout();
          }
          // Altri errori (es. rete): non cancellare i token; l'utente può ricaricare
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!refreshToken) return;

    const REFRESH_INTERVAL_MS = 20 * 60 * 1000;

    const intervalId = setInterval(() => {
      refreshAccessToken();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [refreshToken, refreshAccessToken]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    
    const { token: newToken, refresh_token: newRefreshToken, user: userData } = response.data;
    localStorage.setItem("affidya_token", newToken);
    if (newRefreshToken) {
      localStorage.setItem("affidya_refresh_token", newRefreshToken);
      setRefreshToken(newRefreshToken);
    }
    setToken(newToken);
    applyUserData(userData, setUser, setPermissions);
    return userData;
  };

  const completeExternalLogin = useCallback((authData) => {
    const { token: newToken, refresh_token: newRefreshToken, user: userData } = authData;
    localStorage.setItem("affidya_token", newToken);
    if (newRefreshToken) {
      localStorage.setItem("affidya_refresh_token", newRefreshToken);
      setRefreshToken(newRefreshToken);
    }
    setToken(newToken);
    applyUserData(userData, setUser, setPermissions);
    return userData;
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      applyUserData(response.data, setUser, setPermissions);
      return response.data;
    } catch (error) {
      console.error("Refresh user failed:", error);
      throw error;
    }
  }, [token]);

  const switchStruttura = async (strutturaId) => {
    const strutture = user?.strutture || [];
    const target = strutture.find((s) => s.id === strutturaId);
    if (!target) {
      throw new Error("Struttura non valida");
    }
    setLocalActiveStrutturaId(strutturaId);
    setUser((prev) => {
      const enriched = enrichUserWithLocalStruttura({
        ...prev,
        activeStrutturaId: strutturaId,
      });
      updateStrutturaConfig(
        enriched.strutture?.find((s) => s.id === strutturaId) || target
      );
      return enriched;
    });
    // Allinea flag per-struttura (es. manutenzioni_enabled) dal backend
    try {
      await refreshUser();
    } catch {
      /* keep local switch even if refresh fails */
    }
    return { success: true, message: `Struttura attiva: ${target.name}` };
  };

  const updateUser = (updates) => {
    setUser(prevUser => {
      const newUser = { ...prevUser, ...updates };
      const activeStruttura = newUser.strutture?.find(
        (s) => s.id === newUser.activeStrutturaId
      ) || newUser.strutture?.[0] || null;
      updateStrutturaConfig(activeStruttura || newUser);
      return newUser;
    });
  };

  // Da lista strutture (multi-struttura) oppure fallback per account con una sola struttura
  // (backend può restituire struttura_id/zohoRecordId ma strutture=[] per org con una sola struttura o legacy)
  const effectiveId = user?.struttura_id || user?.zohoRecordId;
  const activeStruttura =
    user?.strutture?.find((s) => s.id === user?.activeStrutturaId) ||
    user?.strutture?.[0] ||
    (effectiveId
      ? {
          id: effectiveId,
          name: user.facility_name || "Struttura",
          zohoRecordId: user.zohoRecordId || null,
        }
      : null);

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const hasAllPermissions = (...perms) => {
    return perms.every(p => permissions.includes(p));
  };

  const hasAnyPermission = (...perms) => {
    return perms.some(p => permissions.includes(p));
  };

  const getRoleDisplayName = () => {
    const normalizedRole = normalizeRole(user?.role);
    const roleNames = {
      super_admin: "Super Admin",
      coordinatore: "Coordinatore",
      operatore: "Operatore",
    };
    return roleNames[normalizedRole] || user?.role || "Utente";
  };

  const value = {
    user,
    token,
    login,
    completeExternalLogin,
    logout,
    syncSessionFromStorage,
    updateUser,
    refreshUser,
    loading,
    isAuthenticated: !!user,
    impersonating,
    // RBAC helpers
    permissions,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    getRoleDisplayName,
    // Multi-struttura
    strutture: user?.strutture || [],
    activeStruttura,
    switchStruttura,
    planTier: user?.plan_tier,
    maxStrutture: user?.max_strutture,
    accountStatus: user?.account_status,
    trialExpiresAt: user?.trial_expires_at,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
